import React, { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "@mui/material";
import dynamic from "next/dynamic";

import Navbar from "./Navbar";
import About from "./About";
import WhatYouWillLearn from "./WhatYouWillLearn";
import CourseContent from "./CourseContent";

const Testimonials = dynamic(
  () => import("./../../../bootcamps/components/common/Testimonials"),
  {
    ssr: false,
  }
);

enum TabIdx {
  ABOUT = 0,
  WHAT_YOU_WILL_LEARN = 1,
  COURSE_CONTENT = 2,
  SUCCESS_STORIES = 3,
}

const DesktopDetailSection: React.FC<NSCourses.IDetailSectionProps> = ({
  course,
}) => {
  const [tabIdx, setTabIdx] = useState(TabIdx.ABOUT);

  const aboutRef = useRef<HTMLDivElement | null>(null);
  const whatWillYouLearnRef = useRef<HTMLDivElement | null>(null);
  const courseContent = useRef<HTMLDivElement | null>(null);
  const successStoriesRef = useRef<HTMLDivElement | null>(null);

  const onTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabIdx(newValue);
  };

  const switchTab = useMemo(
    () =>
      debounce((tab: TabIdx, id: string) => {
        setTabIdx((tabIdx) => {
          if (tabIdx !== tab) {
            // TODO:
            // router.push(`#${id}`, undefined, {
            //   shallow: true,
            // });
            return tab;
          }
          return tabIdx;
        });
      }, 100),
    []
  );

  useEffect(() => {
    const options: IntersectionObserverInit = {
      root: null,
      threshold: 0.1,
    };

    const observerHandler: IntersectionObserverCallback = (
      entries: IntersectionObserverEntry[],
      observer: IntersectionObserver
    ) => {
      const intersecting = entries.filter(
        (entry) => entry.isIntersecting && entry.intersectionRatio > 0.1
      );
      if (!intersecting.length) return;
      const entry = intersecting[0];
      const id = entry.target.id as
        | "about"
        | "what-will-you-learn"
        | "course-content"
        | "success-stories";

      const newTab =
        {
          about: TabIdx.ABOUT,
          ["what-will-you-learn"]: TabIdx.WHAT_YOU_WILL_LEARN,
          ["course-content"]: TabIdx.COURSE_CONTENT,
          ["success-stories"]: TabIdx.SUCCESS_STORIES,
        }[id] || TabIdx.ABOUT;

      switchTab(newTab, id);
    };

    const observer = new IntersectionObserver(observerHandler, options);

    if (aboutRef.current) observer.observe(aboutRef.current);
    if (whatWillYouLearnRef.current)
      observer.observe(whatWillYouLearnRef.current);
    if (courseContent.current) observer.observe(courseContent.current);
    if (successStoriesRef.current) observer.observe(successStoriesRef.current);

    return () => {
      observer.disconnect();
    };
  }, [switchTab]);

  return (
    <div>
      <Navbar
        onTabChange={onTabChange}
        tabIdx={tabIdx}
        userEnrolled={!!course.user_enrolled}
        slug={course.slug}
        enrollUrl={course.enroll_url}
      />
      <div ref={aboutRef} id="about">
        {course?.about && (
          <About
            about={course.about}
            courseInstructors={course.course_instructors}
          />
        )}
      </div>
      <div ref={whatWillYouLearnRef} id="what-will-you-learn">
        <WhatYouWillLearn content={course.what_will_you_learn} />
      </div>
      <div ref={courseContent} id="course-content">
        <CourseContent
          modules={course.modules}
          userEnrolled={!!course.user_enrolled}
          slug={course.slug}
        />
      </div>
      <div ref={successStoriesRef} id="success-stories">
        <Testimonials />
      </div>
    </div>
  );
};

export default DesktopDetailSection;
